import { JobStatus } from "../service/job"
import { JobSummary } from "../service/job-summary"
import { JobTrait } from "../service/job-trait"

/**
 * One row in the Företagsjobb picker: either a company (departmentId null)
 * or one of its departments. Rows come in depth-first display order.
 */
export interface CompanyJobFilterNode {
  companyId: string
  departmentId: string | null
  name: string
  // Full path, e.g. "Acme AB › Region Syd › Malmö". Unique per store.
  pathLabel: string
  // 0 = company, 1 = top-level department, …
  depth: number
  // Department counts include every descendant department.
  uninvoicedCount: number
}

export interface CompanyJobFilterContactPerson {
  id: string
  companyId: string
  // Root → leaf department ids the contact person sits under.
  departmentPathIds: string[]
  label: string
  uninvoicedCount: number
}

export interface CompanyJobFilterOptions {
  totalUninvoicedCount: number
  nodes: CompanyJobFilterNode[]
  contactPersons: CompanyJobFilterContactPerson[]
}

// "uninvoiced" = not marked Fakturerat and not cancelled (a cancelled company
// job will never be invoiced).
// "addableToInvoice" = neither marked Fakturerat nor on an invoice — the jobs
// the backend accepts when creating a fakturaunderlag or adding to one.
export const COMPANY_JOBS_INVOICED_FILTERS = [
  "all",
  "uninvoiced",
  "invoiced",
  "belongsToInvoice",
  "doesNotBelongToInvoice",
  "addableToInvoice",
] as const

export type CompanyJobsInvoicedFilter =
  (typeof COMPANY_JOBS_INVOICED_FILTERS)[number]

const COMPANY_JOBS_INVOICED_FILTER_SET: ReadonlySet<string> = new Set(
  COMPANY_JOBS_INVOICED_FILTERS,
)

export function isCompanyJobsInvoicedFilter(
  value: string,
): value is CompanyJobsInvoicedFilter {
  return COMPANY_JOBS_INVOICED_FILTER_SET.has(value)
}

export type CompanyJobsStatusFilterValue = JobStatus | JobTrait

export type CompanyJobsStatusFilterMode = "include" | "exclude"

export interface CompanyJobsPage {
  jobs: JobSummary[]
  hasMore: boolean
  // Jobs matching the scope and filters, across all pages.
  totalCount: number
}

// Counts for the selected company scope, ignoring the status and invoiced
// filters. A status count matches what that status filter would return.
export interface CompanyJobCounts {
  total: number
  uninvoiced: number
  invoiced: number
  belongsToInvoice: number
  doesNotBelongToInvoice: number
  byStatus: Record<CompanyJobsStatusFilterValue, number>
}
